import { DOIFileHeaderSelect } from "components/DOIHeaderSelect/DOIFileHeaderSelect";
import { DOIHeaderSelect } from "components/DOIHeaderSelect/DOIHeaderSelect";

import type {
  AmendedReviewScreeningType,
  CostProjectionDataType,
  FileType,
  MSADataType,
} from "../../../../types/DataTypes";
import { ClaimNumbersDisplay } from "../components/ClaimNumbersDisplay";
import ClickableServiceType from "../components/ClickableServiceType";
import FileNumberPill from "../components/FileNumberPill";
import ServicesPills from "../components/ServicesPills";
import type { InfoPillFields } from "../InfoPillTypes";
import {
  formatDateOfBirth,
  formatMedicareEligibilityStatus,
  getClaimNumbersString,
  getDatesOfInjuryInfo,
  getEmployerName,
  NONE,
  NOT_AVAILABLE,
} from "../InfoPillTypes";

// Future Medical Overview Page
export const getOverviewDataForInfoPill = (
  fileData?: FileType,
): InfoPillFields => {
  if (!fileData) return {};

  return {
    "File #": {
      value: fileData?.id ? (
        <FileNumberPill fileId={fileData.id} />
      ) : (
        NOT_AVAILABLE
      ),
      disableTooltip: true,
    },
    Adjuster: {
      value: NOT_AVAILABLE,
    },
    Claimant: {
      value: fileData?.claimants?.[0]
        ? `${fileData.claimants[0].first_name} ${fileData.claimants[0].last_name}`
        : NOT_AVAILABLE,
      action: fileData?.section_111_claimant_url
        ? {
            type: "custom" as const,
            handler: () => {
              window.open(fileData.section_111_claimant_url || undefined);
            },
          }
        : undefined,
    },
    Client: {
      value: fileData?.client?.name || NOT_AVAILABLE,
      action: {
        type: "drawer" as const,
        drawerType: "clientInfo" as const,
      },
    },
    [getDatesOfInjuryInfo(fileData?.dates_of_injury).label]: {
      value: getDatesOfInjuryInfo(fileData?.dates_of_injury).value,
    },
    Services: {
      value: fileData?.services?.length ? (
        <ServicesPills services={fileData.services} fileId={fileData.id} />
      ) : (
        NOT_AVAILABLE
      ),
      disableTooltip: true,
    },
    Employer: {
      value: fileData?.employer?.name || NOT_AVAILABLE,
    },
    "Medicare Eligibility Status": {
      value: formatMedicareEligibilityStatus(
        fileData?.claimants?.[0]?.is_medicare_eligible,
      ),
    },
    "Next Action": {
      value: "Query",
    },
    NSA: {
      value: NONE,
    },
    TPA: {
      value: NOT_AVAILABLE,
    },
    "File Adjuster": {
      value: fileData?.adjuster?.name || NOT_AVAILABLE,
    },
  };
};

// MSA and Cost Projection detail pages
export const getMedicalServiceInfoPillData = (
  service?: MSADataType | CostProjectionDataType,
): InfoPillFields => {
  if (!service) return {};

  const fields: InfoPillFields = {
    "File #": {
      value: service?.file?.id ? (
        <FileNumberPill fileId={service.file.id} />
      ) : (
        NOT_AVAILABLE
      ),
      disableTooltip: true,
    },
    Claimant: {
      value: service?.claimant
        ? `${service.claimant.first_name} ${service.claimant.last_name}`
        : NONE,
      action: service?.section_111_claimant_url
        ? {
            type: "custom" as const,
            handler: () => {
              window.open(service.section_111_claimant_url || undefined);
            },
          }
        : undefined,
    },
    "Claim Numbers": {
      value: <ClaimNumbersDisplay service={service} />,
      tooltip: getClaimNumbersString(
        service?.file?.claim_numbers,
        service?.file?.dates_of_injury,
      ),
      tooltipHtml: true,
    },
    Client: {
      value: service?.file?.client.name || NONE,
      action: {
        type: "drawer" as const,
        drawerType: "clientInfo" as const,
      },
    },
    DOB: {
      value: formatDateOfBirth(service?.claimant?.birth_date),
    },
    "Date of Injury": {
      value: <DOIHeaderSelect service={service} />,
      tooltip: getClaimNumbersString(
        service?.file?.claim_numbers,
        service?.file?.dates_of_injury,
      ),
      tooltipHtml: true,
    },
    Services: {
      value: service?.file?.services?.length ? (
        <ServicesPills
          services={service.file.services}
          fileId={service.file.id}
        />
      ) : (
        NOT_AVAILABLE
      ),
      disableTooltip: true,
    },
    Employer: {
      value: getEmployerName(service?.file || {}),
    },
    "Medicare Eligibility Status": {
      value: formatMedicareEligibilityStatus(
        service?.claimant?.is_medicare_eligible,
      ),
    },
    "Medicare ID": {
      value: service?.claimant?.medicare_id || NONE,
    },
    "File Adjuster": {
      value: service?.file?.adjuster?.name || NOT_AVAILABLE,
    },
  };

  // only add AK Manager if it exists (MSA specific)
  if ("service_assignee" in service && service.service_assignee) {
    fields["AK Manager"] = {
      value: service.service_assignee.user_full_name || NONE,
    };

    fields["File Adjuster"] = {
      value: service?.file?.adjuster?.name || NONE,
    };
  }

  // add Service Type - make it clickable for convertible FM services (MSA, LCP, MCP, ARV)
  const convertibleServiceTypes = [
    "Medicare Set-Aside",
    "MSA-111",
    "Non-Threshold MSA",
    "Pre-MSA Snapshot",
    "Amended Review",
    "LCP",
    "MCP",
  ];
  if (service.type && convertibleServiceTypes.includes(service.type)) {
    fields["Service Type"] = {
      value: service.id ? (
        <ClickableServiceType
          serviceType={service.type || NONE}
          serviceId={service.id.toString()}
        />
      ) : (
        service.type || NONE
      ),
    };
  } else {
    // non-convertible service types show as plain text
    fields["Service Type"] = {
      value: service.type || NONE,
    };
  }

  return fields;
};

// Amended Review Screening detail page
export const getAmendedReviewScreeningInfoPillData = (
  service?: AmendedReviewScreeningType,
): InfoPillFields => {
  if (!service) return {};

  // ARS uses file.claimants[0] instead of direct claimant field
  const claimant = service?.file?.claimants?.[0];

  const fields: InfoPillFields = {
    "File #": {
      value: service?.file?.id ? (
        <FileNumberPill fileId={service.file.id} />
      ) : (
        NOT_AVAILABLE
      ),
      disableTooltip: true,
    },
    Claimant: {
      value: claimant ? `${claimant.first_name} ${claimant.last_name}` : NONE,
      action: service?.section_111_claimant_url
        ? {
            type: "custom" as const,
            handler: () => {
              window.open(service.section_111_claimant_url || undefined);
            },
          }
        : undefined,
    },
    "Claim Numbers": {
      value: <ClaimNumbersDisplay service={service} />,
      tooltip: getClaimNumbersString(
        service?.file?.claim_numbers,
        service?.file?.dates_of_injury,
      ),
      tooltipHtml: true,
    },
    Client: {
      value: service?.file?.client.name || NONE,
      action: {
        type: "drawer" as const,
        drawerType: "clientInfo" as const,
      },
    },
    DOB: {
      value: formatDateOfBirth(claimant?.birth_date),
    },
    "Date of Injury": {
      value: <DOIFileHeaderSelect service={service} />,
      tooltip: getClaimNumbersString(
        service?.file?.claim_numbers,
        service?.file?.dates_of_injury,
      ),
      tooltipHtml: true,
    },
    Services: {
      value: service?.file?.services?.length ? (
        <ServicesPills
          services={service.file.services}
          fileId={service.file.id}
        />
      ) : (
        NOT_AVAILABLE
      ),
      disableTooltip: true,
    },
    Employer: {
      value: getEmployerName(service?.file || {}),
    },
    "Medicare Eligibility Status": {
      value: formatMedicareEligibilityStatus(claimant?.is_medicare_eligible),
    },
    "Medicare ID": {
      value: claimant?.medicare_id || NONE,
    },
    "File Adjuster": {
      value: service?.file?.adjuster?.name || NOT_AVAILABLE,
    },
  };

  // only add AK Manager if it exists (MSA specific)
  if ("service_assignee" in service && service.service_assignee) {
    fields["AK Manager"] = {
      value:
        (service.service_assignee as { user_full_name: string })
          .user_full_name || NONE,
    };

    fields["File Adjuster"] = {
      value: service?.file?.adjuster?.name || NONE,
    };
  }

  return fields;
};
